import { CstNode, IToken, ITokenConfig, Lexer, TokenType } from 'chevrotain';

import { AllCstTypes } from './generatedSchemaTypes';
import {
  At,
  Colon,
  Equals,
  FloatValue,
  IntValue,
  LCurly,
  LParen,
  NameButNotKeyword,
  RCurly,
  RParen,
  SharedNodeType,
  SharedParser,
  StringValue,
  createKeywordToken as createSharedKeywordToken,
  createToken as createSharedToken,
  sharedKeywordTokens,
  sharedTokens,
} from './parseSharedCST';

const allTokens: TokenType[] = [...sharedTokens];
const createToken = (config: ITokenConfig) =>
  createSharedToken(config, allTokens);

export const keywordTokens: TokenType[] = [...sharedKeywordTokens];
const createKeywordToken = (config: ITokenConfig) =>
  createSharedKeywordToken(config, allTokens, keywordTokens);

// Lexical Tokens, for:
//   https://spec.graphql.org/draft/#sec-Language.Source-Text.Lexical-Tokens
const And = createToken({ name: 'And', pattern: '&' });
const VerticalLine = createToken({ name: 'VerticalLine', pattern: '|' });

const Repeatable = createKeywordToken({
  name: 'Repeatable',
  pattern: 'repeatable',
});
const On = createKeywordToken({ name: 'On', pattern: 'on' });
const Schema = createKeywordToken({ name: 'Schema', pattern: 'schema' });
const Extend = createKeywordToken({ name: 'Extend', pattern: 'extend' });
const Scalar = createKeywordToken({ name: 'Scalar', pattern: 'scalar' });
const Implements = createKeywordToken({
  name: 'Implements',
  pattern: 'implements',
});
const Interface = createKeywordToken({
  name: 'Interface',
  pattern: 'interface',
});
const Union = createKeywordToken({ name: 'Union', pattern: 'union' });
const Enum = createKeywordToken({ name: 'Enum', pattern: 'enum' });
const Input = createKeywordToken({ name: 'Input', pattern: 'input' });
const DirectiveTok = createKeywordToken({
  name: 'DirectiveTok',
  pattern: 'directive',
});
const TypeTok = createKeywordToken({ name: 'TypeTok', pattern: 'type' });

// Tokens for directive locations, for:
//   https://spec.graphql.org/draft/#ExecutableDirectiveLocation
//   https://spec.graphql.org/draft/#TypeSystemDirectiveLocation
const QUERY = createKeywordToken({ name: 'QUERY', pattern: 'QUERY' });
const MUTATION = createKeywordToken({
  name: 'MUTATION',
  pattern: 'MUTATION',
});
const SUBSCRIPTION = createKeywordToken({
  name: 'SUBSCRIPTION',
  pattern: 'SUBSCRIPTION',
});
const FRAGMENT_DEFINITION = createKeywordToken({
  name: 'FRAGMENT_DEFINITION',
  pattern: 'FRAGMENT_DEFINITION',
});
const FRAGMENT_SPREAD = createKeywordToken({
  name: 'FRAGMENT_SPREAD',
  pattern: 'FRAGMENT_SPREAD',
});
const INLINE_FRAGMENT = createKeywordToken({
  name: 'INLINE_FRAGMENT',
  pattern: 'INLINE_FRAGMENT',
});
const SCHEMA = createKeywordToken({ name: 'SCHEMA', pattern: 'SCHEMA' });
const SCALAR = createKeywordToken({ name: 'SCALAR', pattern: 'SCALAR' });
const OBJECT = createKeywordToken({ name: 'OBJECT', pattern: 'OBJECT' });
const FIELD_DEFINITION = createKeywordToken({
  name: 'FIELD_DEFINITION',
  pattern: 'FIELD_DEFINITION',
});
const FIELD = createKeywordToken({ name: 'FIELD', pattern: 'FIELD' });
const ARGUMENT_DEFINITION = createKeywordToken({
  name: 'ARGUMENT_DEFINITION',
  pattern: 'ARGUMENT_DEFINITION',
});
const INTERFACE = createKeywordToken({
  name: 'INTERFACE',
  pattern: 'INTERFACE',
});
const UNION = createKeywordToken({ name: 'UNION', pattern: 'UNION' });
const ENUM_VALUE = createKeywordToken({
  name: 'ENUM_VALUE',
  pattern: 'ENUM_VALUE',
});
const ENUM = createKeywordToken({ name: 'ENUM', pattern: 'ENUM' });
const INPUT_OBJECT = createKeywordToken({
  name: 'INPUT_OBJECT',
  pattern: 'INPUT_OBJECT',
});
const INPUT_FIELD_DEFINITION = createKeywordToken({
  name: 'INPUT_FIELD_DEFINITION',
  pattern: 'INPUT_FIELD_DEFINITION',
});

// #NameButNotKeyword We manually add the general Identifier (Name) AFTER all the keyword token types.
allTokens.push(NameButNotKeyword);
const GraphQLLexer = new Lexer(allTokens);

export type SdlSpecificNodeType =
  | 'TypeSystemExtensionDocument'
  | 'TypeSystemDefinitionOrExtension'
  | 'Value'
  | 'SchemaDefinition'
  | 'TypeDefinition'
  | 'DirectiveDefinition'
  | 'SchemaExtension'
  | 'TypeExtension'
  | 'RootOperationTypeDefinition'
  | 'RootOperationTypeDefinitionList'
  | 'ScalarTypeDefinition'
  | 'ObjectTypeDefinition'
  | 'InterfaceTypeDefinition'
  | 'UnionTypeDefinition'
  | 'EnumTypeDefinition'
  | 'InputObjectTypeDefinition'
  | 'ScalarTypeExtension'
  | 'ObjectTypeExtension'
  | 'InterfaceTypeExtension'
  | 'UnionTypeExtension'
  | 'EnumTypeExtension'
  | 'InputObjectTypeExtension'
  | 'Description'
  | 'ImplementsInterfaces'
  | 'FieldsDefinition'
  | 'ArgumentsDefinition'
  | 'InputValueDefinition'
  | 'FieldDefinition'
  | 'UnionMemberTypes'
  | 'EnumValuesDefinition'
  | 'InputFieldsDefinition'
  | 'DirectiveLocations'
  | 'ExecutableDirectiveLocation'
  | 'TypeSystemDirectiveLocation'
  | 'EnumValueDefinition'
  | 'DirectiveLocation';

export type SdlNodeType = SdlSpecificNodeType | SharedNodeType;

type CstParserRules = { [nodeType in SdlNodeType]: (idx?: number) => CstNode };

export class SdlParser extends SharedParser<SdlSpecificNodeType> {
  constructor() {
    super(allTokens, keywordTokens);
  }

  addRulesToParser() {
    // ##AddRulesToParser TODO (jason) could not get this class to have a union
    // index signature, this should be safe though
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $: SdlParser & CstParserRules = this as any;

    // The parsing methods

    // https://spec.graphql.org/draft/#TypeSystemExtensionDocument
    //   TypeSystemExtensionDocument :
    //     TypeSystemDefinitionOrExtension[list]
    $.RULE('TypeSystemExtensionDocument', () => {
      $.MANY(() => {
        $.SUBRULE($.TypeSystemDefinitionOrExtension);
      });
    });

    // https://spec.graphql.org/draft/#TypeSystemDefinitionOrExtension
    //   TypeSystemDefinitionOrExtension :
    //     TypeSystemDefinition
    //     TypeSystemExtension
    //
    // https://spec.graphql.org/draft/#TypeSystemDefinition
    //   TypeSystemDefinition :
    //     SchemaDefinition
    //     TypeDefinition
    //     DirectiveDefinition
    //
    // https://spec.graphql.org/draft/#TypeSystemExtension
    //   TypeSystemExtension :
    //     SchemaExtension
    //     TypeExtension
    //
    // The rules for TypeSystemDefinitionOrExtension were reduced (they make
    // more sense to be separate when the SDL and Executable grammars are
    // shared)
    $.RULE('TypeSystemDefinitionOrExtension', () => {
      $.OR([
        { ALT: () => $.SUBRULE($.SchemaDefinition) },
        { ALT: () => $.SUBRULE($.TypeDefinition) },
        { ALT: () => $.SUBRULE($.DirectiveDefinition) },
        { ALT: () => $.SUBRULE($.SchemaExtension) },
        { ALT: () => $.SUBRULE($.TypeExtension) },
      ]);
    });

    // https://spec.graphql.org/draft/#Value
    //   Value[const]:
    //     [If not const] Variable
    //     IntValue
    //     FloatValue
    //     StringValue
    //     BooleanValue
    //     NullValue
    //     EnumValue
    //     ListValue [?const]
    //     ObjectValue [?const]
    //
    // We do not include '[If not const] Variable' here because there are
    // variables in the SDL grammar (only Executable)
    $.RULE('Value', () => {
      $.OR<CstNode | IToken>([
        { ALT: () => $.CONSUME(IntValue) },
        { ALT: () => $.CONSUME(FloatValue) },
        { ALT: () => $.CONSUME(StringValue) },
        { ALT: () => $.SUBRULE($.BooleanValue) },
        { ALT: () => $.SUBRULE($.NullValue) },
        { ALT: () => $.SUBRULE($.EnumValue) },
        // Args here represent 'isConst'
        { ALT: () => $.SUBRULE($.ListValue, { ARGS: [true] }) },
        { ALT: () => $.SUBRULE($.ObjectValue, { ARGS: [true] }) },
      ]);
    });

    // https://spec.graphql.org/draft/#SchemaDefinition
    //   SchemaDefinition :
    //     Description[opt] schema Directives[const][opt] { RootOperationTypeDefinition[list] }
    $.RULE('SchemaDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.CONSUME(Schema);
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
      $.CONSUME(LCurly);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.RootOperationTypeDefinition);
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#SchemaExtension
    //   SchemaExtension :
    //     extend schema Directives[const][opt] { RootOperationTypeDefinition[list] }
    //     extend schema Directives[const] [lookahead != '{']
    //
    // This is slightly different than the grammar in that the first branch is
    // non optional Directives with optional RootOperationTypeDefinitionlist,
    // and the second is the non optional RootOperationTypeDefinitionlist. These
    // both model the same three states, but this does not rely on a lookahead:
    // - Directives and RootOperationTypeDefinitionlist
    // - Just Directives
    // - Just RootOperationTypeDefinitionlist
    // - *Does not model* neither Directives or RootOperationTypeDefinitionlist
    //
    // This also references RootOperationTypeDefinitionList instead of
    // RootOperationTypeDefinition[list] (explained below in
    // RootOperationTypeDefinitionList)
    $.RULE('SchemaExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Schema);
      $.OR([
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE($.Directives, { ARGS: [true] });
            $.OPTION(() => {
              $.SUBRULE($.RootOperationTypeDefinitionList);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE2($.RootOperationTypeDefinitionList);
          },
        },
      ]);
    });

    // Not in spec: represents "{ RootOperationTypeDefinition[list] }"
    //
    // Because SchemaExtension needed to be rewritten, this is sharing the
    // repeated portion of the rewritten rule
    $.RULE('RootOperationTypeDefinitionList', () => {
      $.CONSUME(LCurly);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.RootOperationTypeDefinition);
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#RootOperationTypeDefinition
    //   RootOperationTypeDefinition :
    //     OperationType : NamedType
    $.RULE('RootOperationTypeDefinition', () => {
      $.SUBRULE($.OperationType);
      $.CONSUME(Colon);
      $.SUBRULE($.NamedType);
    });

    // https://spec.graphql.org/draft/#Description
    //   Description :
    //     StringValue
    $.RULE('Description', () => {
      $.CONSUME(StringValue);
    });

    // https://spec.graphql.org/draft/#TypeDefinition
    //   TypeDefinition :
    //     ScalarTypeDefinition
    //     ObjectTypeDefinition
    //     InterfaceTypeDefinition
    //     UnionTypeDefinition
    //     EnumTypeDefinition
    //     InputObjectTypeDefinition
    //
    // Description is added here since every type definition had description as
    // part of their parse rule. It is here instead so we don't have an extra
    // lookahead (every child rule starts with a different token)
    $.RULE('TypeDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.OR([
        { ALT: () => $.SUBRULE($.ScalarTypeDefinition) },
        { ALT: () => $.SUBRULE($.ObjectTypeDefinition) },
        { ALT: () => $.SUBRULE($.InterfaceTypeDefinition) },
        { ALT: () => $.SUBRULE($.UnionTypeDefinition) },
        { ALT: () => $.SUBRULE($.EnumTypeDefinition) },
        { ALT: () => $.SUBRULE($.InputObjectTypeDefinition) },
      ]);
    });

    // https://spec.graphql.org/draft/#TypeExtension
    //   TypeExtension :
    //     ScalarTypeExtension
    //     ObjectTypeExtension
    //     InterfaceTypeExtension
    //     UnionTypeExtension
    //     EnumTypeExtension
    //     InputObjectTypeExtension
    $.RULE('TypeExtension', () => {
      $.OR([
        { ALT: () => $.SUBRULE($.ScalarTypeExtension) },
        { ALT: () => $.SUBRULE($.ObjectTypeExtension) },
        { ALT: () => $.SUBRULE($.InterfaceTypeExtension) },
        { ALT: () => $.SUBRULE($.UnionTypeExtension) },
        { ALT: () => $.SUBRULE($.EnumTypeExtension) },
        { ALT: () => $.SUBRULE($.InputObjectTypeExtension) },
      ]);
    });

    // https://spec.graphql.org/draft/#ScalarTypeDefinition
    //   ScalarTypeDefinition :
    //     Description[opt] scalar Name Directives[const][opt]
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('ScalarTypeDefinition', () => {
      $.CONSUME(Scalar);
      $.SUBRULE($.Name);
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
    });

    // https://spec.graphql.org/draft/#ScalarTypeExtension
    //   ScalarTypeExtension :
    //     extend scalar Name Directives[const]
    $.RULE('ScalarTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Scalar);
      $.SUBRULE($.Name);
      // Args here represent 'isConst'
      $.SUBRULE($.Directives, { ARGS: [true] });
    });

    // https://spec.graphql.org/draft/#ObjectTypeDefinition
    //   ObjectTypeDefinition :
    //     Description[opt] type Name ImplementsInterfaces[opt] Directives[const][opt] FieldsDefinition
    //     Description[opt] type Name ImplementsInterfaces[opt] Directives[const][opt] [lookahead != '{']
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('ObjectTypeDefinition', () => {
      $.CONSUME(TypeTok);
      $.SUBRULE($.Name);

      $.OPTION(() => {
        $.SUBRULE($.ImplementsInterfaces);
      });
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
      $.OPTION3(() => {
        $.SUBRULE($.FieldsDefinition);
      });
    });

    // https://spec.graphql.org/draft/#ObjectTypeExtension
    //   ObjectTypeExtension :
    //     extend type Name Implements Interfaces[opt] Directives[const][opt] FieldsDefinition
    //     extend type Name Implements Interfaces[opt] Directives[const] [lookahead != '{']
    //     extend type Name Implements Interfaces [lookahead != '{']
    //
    // The branches here have different start tokens, so there is no lookahead
    // needed. The above grammar represents 'as least one of Interfaces,
    // Directives, or FieldsDefinition' must be present.
    $.RULE('ObjectTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(TypeTok);
      $.SUBRULE($.Name);

      $.OR([
        {
          ALT: () => {
            $.SUBRULE($.ImplementsInterfaces);
            $.OPTION(() => {
              // Args here represent 'isConst'
              $.SUBRULE($.Directives, { ARGS: [true] });
            });
            $.OPTION2(() => {
              $.SUBRULE($.FieldsDefinition);
            });
          },
        },
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE2($.Directives, { ARGS: [true] });
            $.OPTION3(() => {
              $.SUBRULE2($.FieldsDefinition);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE3($.FieldsDefinition);
          },
        },
      ]);
    });

    // https://spec.graphql.org/draft/#ImplementsInterfaces
    //   ImplementsInterfaces :
    //     ImplementsInterfaces & NamedType
    //     implements &[opt] NamedType
    //
    // This represents the 'recursive' definition of ImplementsInterfaces as a
    // list instead. This cannot use AT_LEAST_ONE because the first & is
    // optional
    $.RULE('ImplementsInterfaces', () => {
      $.CONSUME(Implements);
      $.OPTION(() => {
        $.CONSUME(And);
      });
      $.SUBRULE($.NamedType);
      $.MANY(() => {
        $.CONSUME2(And);
        $.SUBRULE2($.NamedType);
      });
    });

    // https://spec.graphql.org/draft/#FieldsDefinition
    //   FieldsDefinition :
    //     { FieldDefinition[list] }
    $.RULE('FieldsDefinition', () => {
      $.CONSUME(LCurly);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.FieldDefinition);
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#FieldDefinition
    //   FieldDefinition :
    //     Description[opt] Name ArgumentsDefinition[opt] : Type Directives[const][opt]
    $.RULE('FieldDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.SUBRULE($.Name);
      $.OPTION2(() => {
        $.SUBRULE($.ArgumentsDefinition);
      });
      $.CONSUME(Colon);
      $.SUBRULE($.Type);
      $.OPTION3(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
    });

    // https://spec.graphql.org/draft/#ArgumentsDefinition
    //   ArgumentsDefinition :
    //     ( InputValueDefinition[list] )
    $.RULE('ArgumentsDefinition', () => {
      $.CONSUME(LParen);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.InputValueDefinition);
      });
      $.CONSUME(RParen);
    });

    // https://spec.graphql.org/draft/#InputValueDefinition
    //   InputValueDefinition :
    //     Description[opt] Name : Type DefaultValue[opt ]Directives[const][opt]
    $.RULE('InputValueDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.SUBRULE($.Name);
      $.CONSUME(Colon);
      $.SUBRULE($.Type);
      $.OPTION2(() => {
        $.SUBRULE($.DefaultValue);
      });
      $.OPTION3(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
    });

    // https://spec.graphql.org/draft/#InterfaceTypeDefinition
    //   InterfaceTypeDefinition :
    //     Description[opt] interface Name ImplementsInterfaces[opt] Directives[const][opt] FieldsDefinition
    //     Description[opt] interface Name ImplementsInterfaces[opt] Directives[const][opt] [lookahead != '{']
    //
    // TODO this is functionally different. The: "interface Name" seems to be valid
    // in the spec, but is not represented in any of the cases below. I think
    // this should be updated
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('InterfaceTypeDefinition', () => {
      $.CONSUME(Interface);
      $.SUBRULE($.Name);

      $.OPTION(() => {
        $.SUBRULE($.ImplementsInterfaces);
      });
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
      $.OPTION3(() => {
        $.SUBRULE($.FieldsDefinition);
      });
    });

    // https://spec.graphql.org/draft/#InterfaceTypeExtension
    //   InterfaceTypeExtension :
    //     extend interface Name Implements Interfaces[opt] Directives[const][opt] FieldsDefinition
    //     extend interface Name Implements Interfaces[opt] Directives[const] [lookahead != '{']
    //     extend interface Name Implements Interfaces [lookahead != '{']
    //
    // The branches here have different start tokens, so there is no lookahead
    // needed. The above grammar represents 'as least one of Interfaces,
    // Directives, or FieldsDefinition' must be present.
    $.RULE('InterfaceTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Interface);
      $.SUBRULE($.Name);

      $.OR([
        {
          ALT: () => {
            $.SUBRULE($.ImplementsInterfaces);
            $.OPTION(() => {
              // Args here represent 'isConst'
              $.SUBRULE($.Directives, { ARGS: [true] });
            });
            $.OPTION2(() => {
              $.SUBRULE($.FieldsDefinition);
            });
          },
        },
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE2($.Directives, { ARGS: [true] });
            $.OPTION3(() => {
              $.SUBRULE2($.FieldsDefinition);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE3($.FieldsDefinition);
          },
        },
      ]);
    });

    // https://spec.graphql.org/draft/#UnionTypeDefinition
    //   UnionTypeDefinition :
    //     Description[opt] union Name Directives[const][opt] UnionMemberTypes[opt]
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('UnionTypeDefinition', () => {
      $.CONSUME(Union);
      $.SUBRULE($.Name);
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
      $.OPTION3(() => {
        $.SUBRULE($.UnionMemberTypes);
      });
    });

    // https://spec.graphql.org/draft/#UnionMemberTypes
    //   UnionMemberTypes :
    //      UnionMemberTypes | NamedType
    //      = |[opt] NamedType
    //
    // This represents the 'recursive' definition of UnionMemberTypes as a
    // list instead. This cannot use AT_LEAST_ONE because the first | is
    // optional
    $.RULE('UnionMemberTypes', () => {
      $.CONSUME(Equals);

      $.OPTION(() => {
        $.CONSUME(VerticalLine);
      });
      $.SUBRULE($.NamedType);

      $.MANY(() => {
        $.CONSUME2(VerticalLine);
        $.SUBRULE2($.NamedType);
      });
    });

    // https://spec.graphql.org/draft/#UnionTypeExtension
    //   UnionTypeExtension :
    //     extend union Name Directives[const][opt] UnionMemberTypes
    //     extend union Name Directives[const]
    //
    // The branches here have different start tokens, so there is no lookahead
    // needed. The above grammar represents 'as least one of Directives or
    // UnionMemberTypes must be present'.
    $.RULE('UnionTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Union);
      $.SUBRULE($.Name);

      // Refactored the grammar to be LL(K)
      $.OR([
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE($.Directives, { ARGS: [true] });
            $.OPTION(() => {
              $.SUBRULE($.UnionMemberTypes);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE2($.UnionMemberTypes);
          },
        },
      ]);
    });

    // https://spec.graphql.org/draft/#EnumTypeDefinition
    //   EnumTypeDefinition :
    //     Description[opt] enum Name Directives[const][opt] EnumValuesDefinition
    //     Description[opt] enum Name Directives[const][opt] [lookahead != '{']
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('EnumTypeDefinition', () => {
      $.CONSUME(Enum);
      $.SUBRULE($.Name);
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
      $.OPTION3(() => {
        $.SUBRULE($.EnumValuesDefinition);
      });
    });

    // https://spec.graphql.org/draft/#EnumValuesDefinition
    //   EnumValuesDefinition :
    //     { EnumValueDefinition[list] }
    $.RULE('EnumValuesDefinition', () => {
      $.CONSUME(LCurly);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.EnumValueDefinition);
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#EnumValueDefinition
    //   EnumValueDefinition :
    //     Description[opt] EnumValue Directives[const][opt]
    $.RULE('EnumValueDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.SUBRULE($.EnumValue);
      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });
    });

    // https://spec.graphql.org/draft/#EnumTypeExtension
    //   EnumTypeExtension :
    //     extend enum Name Directives[const][opt] EnumValuesDefinition
    //     extend enum Name Directives[const] [lookahead != '{']
    //
    // The branches here have different start tokens, so there is no lookahead
    // needed. The above grammar represents 'as least one of Directives or
    // EnumValuesDefinition must be present'.
    $.RULE('EnumTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Enum);
      $.SUBRULE($.Name);

      // Refactored the grammar to be LL(K)
      $.OR([
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE($.Directives, { ARGS: [true] });
            $.OPTION(() => {
              $.SUBRULE($.EnumValuesDefinition);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE2($.EnumValuesDefinition);
          },
        },
      ]);
    });

    // https://spec.graphql.org/draft/#InputObjectTypeDefinition
    //   InputObjectTypeDefinition :
    //     Description[opt] input Name Directives[const][opt] InputFieldsDefinition
    //     Description[opt] input Name Directives[const][opt] [lookahead != '{']
    //
    // Description is on the parent rule (TypeDefinition) instead
    $.RULE('InputObjectTypeDefinition', () => {
      $.CONSUME(Input);
      $.SUBRULE($.Name);

      $.OPTION2(() => {
        // Args here represent 'isConst'
        $.SUBRULE($.Directives, { ARGS: [true] });
      });

      $.OPTION3(() => {
        $.SUBRULE($.InputFieldsDefinition);
      });
    });

    // https://spec.graphql.org/draft/#InputFieldsDefinition
    //   InputFieldsDefinition :
    //     { InputValueDefinition[list] }
    $.RULE('InputFieldsDefinition', () => {
      $.CONSUME(LCurly);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.InputValueDefinition);
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#InputObjectTypeExtension
    //   InputObjectTypeExtension :
    //     extend input Name Directives[const][opt] InputFieldsDefinition
    //     extend input Name Directives[const] [lookahead != '{']
    //
    // The branches here have different start tokens, so there is no lookahead
    // needed. The above grammar represents 'as least one of Directives or
    // InputFieldsDefinition must be present'.
    $.RULE('InputObjectTypeExtension', () => {
      $.CONSUME(Extend);
      $.CONSUME(Input);
      $.SUBRULE($.Name);

      // Refactored the grammar to be LL(K)
      $.OR([
        {
          ALT: () => {
            // Args here represent 'isConst'
            $.SUBRULE($.Directives, { ARGS: [true] });
            $.OPTION(() => {
              $.SUBRULE($.EnumValuesDefinition);
            });
          },
        },
        {
          ALT: () => {
            $.SUBRULE2($.InputFieldsDefinition);
          },
        },
      ]);
    });

    // https://spec.graphql.org/draft/#DirectiveDefinition
    //   DirectiveDefinition :
    //     Description[opt] directive @ Name ArgumentsDefinition[opt] repeatable[opt] on DirectiveLocations
    $.RULE('DirectiveDefinition', () => {
      $.OPTION(() => {
        $.SUBRULE($.Description);
      });
      $.CONSUME(DirectiveTok);
      $.CONSUME(At);
      $.SUBRULE($.Name);
      $.OPTION2(() => {
        $.SUBRULE($.ArgumentsDefinition);
      });
      $.OPTION3(() => {
        $.CONSUME(Repeatable);
      });
      $.CONSUME(On);
      $.OPTION4(() => {
        $.SUBRULE($.DirectiveLocations);
      });
    });

    // https://spec.graphql.org/draft/#DirectiveLocations
    //   DirectiveLocations :
    //     DirectiveLocations | DirectiveLocation
    //     |[opt] DirectiveLocation
    //
    // This represents the 'recursive' definition of DirectiveLocations as a
    // list instead. This cannot use AT_LEAST_ONE because the first | is
    // optional
    $.RULE('DirectiveLocations', () => {
      $.OPTION(() => {
        $.CONSUME(VerticalLine);
      });
      $.SUBRULE($.DirectiveLocation);

      $.MANY(() => {
        $.CONSUME2(VerticalLine);
        $.SUBRULE2($.DirectiveLocation);
      });
    });

    // https://spec.graphql.org/draft/#DirectiveLocation
    //   DirectiveLocation :
    //     ExecutableDirectiveLocation
    //     TypeSystemDirectiveLocation
    $.RULE('DirectiveLocation', () => {
      $.OR([
        { ALT: () => $.SUBRULE($.ExecutableDirectiveLocation) },
        { ALT: () => $.SUBRULE($.TypeSystemDirectiveLocation) },
      ]);
    });

    // https://spec.graphql.org/draft/#ExecutableDirectiveLocation
    //   ExecutableDirectiveLocation : one of
    //     QUERY
    //     MUTATION
    //     SUBSCRIPTION
    //     FIELD
    //     FRAGMENT_DEFINITION
    //     FRAGMENT_SPREAD
    //     INLINE_FRAGMENT
    //     VARIABLE_DEFINITION
    //
    // No VARIABLE_DEFINITION since there are no variables in SDL, only
    // Executable documents
    $.RULE('ExecutableDirectiveLocation', () => {
      $.OR([
        { ALT: () => $.CONSUME(QUERY) },
        { ALT: () => $.CONSUME(MUTATION) },
        { ALT: () => $.CONSUME(SUBSCRIPTION) },
        { ALT: () => $.CONSUME(FIELD) },
        { ALT: () => $.CONSUME(FRAGMENT_DEFINITION) },
        { ALT: () => $.CONSUME(FRAGMENT_SPREAD) },
        { ALT: () => $.CONSUME(INLINE_FRAGMENT) },
      ]);
    });

    // https://spec.graphql.org/draft/#TypeSystemDirectiveLocation
    //   TypeSystemDirectiveLocation : one of
    //     SCHEMA
    //     SCALAR
    //     OBJECT
    //     FIELD_DEFINITION
    //     ARGUMENT_DEFINITION
    //     INTERFACE
    //     UNION
    //     ENUM
    //     ENUM_VALUE
    //     INPUT_OBJECT
    //     INPUT_FIELD_DEFINITION
    $.RULE('TypeSystemDirectiveLocation', () => {
      $.OR([
        { ALT: () => $.CONSUME(SCHEMA) },
        { ALT: () => $.CONSUME(SCALAR) },
        { ALT: () => $.CONSUME(OBJECT) },
        { ALT: () => $.CONSUME(FIELD_DEFINITION) },
        { ALT: () => $.CONSUME(ARGUMENT_DEFINITION) },
        { ALT: () => $.CONSUME(INTERFACE) },
        { ALT: () => $.CONSUME(UNION) },
        { ALT: () => $.CONSUME(ENUM) },
        { ALT: () => $.CONSUME(ENUM_VALUE) },
        { ALT: () => $.CONSUME(INPUT_OBJECT) },
        { ALT: () => $.CONSUME(INPUT_FIELD_DEFINITION) },
      ]);
    });
  }
}

// #AddRulesToParser
export const parser = new SdlParser() as SdlParser & CstParserRules;
export const SdlToken = allTokens;
export function parseSchemaCST(text: string) {
  const lexResult = GraphQLLexer.tokenize(text);
  // setting a new input will RESET the parser instance's state.
  parser.input = lexResult.tokens;
  // any top level rule may be used as an entry point
  const cst: AllCstTypes['TypeSystemExtensionDocument'] =
    parser.TypeSystemExtensionDocument() as AllCstTypes['TypeSystemExtensionDocument'];

  return {
    value: cst,
    lexErrors: lexResult.errors,
    parseErrors: parser.errors,
  };
}
